"use client";

// The feedback sheet (law_sheet_rises): where a report is typed, tagged (bug ·
// feature · question), and media is attached by browse, drag-drop, or paste. It
// is one tall sheet for its whole life, so the writing field takes whatever the
// kinds, the drop zone and the thumbnails leave it. The send pill is the foot's
// corner; whose report it is rides the foot's left, quiet. The draft is
// session-persistent (lib/feedback/draftStore): dismissing to grab a screenshot
// and coming back restores everything.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Sheet from "../os/Sheet";
import { PillButton } from "../ui";
import {
  MAX_FILES,
  addDraftFiles,
  clearDraft,
  getDraftFiles,
  loadDraft,
  saveDraft,
  setDraftFiles,
  subscribeDraftFiles,
  type FeedbackKind,
  type RejectReason,
} from "../../lib/feedback/draftStore";

const KINDS: FeedbackKind[] = ["bug", "feature", "question"];

const REJECT_NOTE: Record<Exclude<RejectReason, null>, string> = {
  count: `up to ${MAX_FILES} files. remove one to add another`,
  size: "that one's over 20mb. a smaller clip works",
  type: "images and videos only",
};

type Phase = "idle" | "sending" | "sent" | "failed";

// small line glyphs — an image/clip mark for the dropzone, a check for the sent pill.
const IcoMedia = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="M4 17l4.5-4.2a2 2 0 0 1 2.7 0L15 16m-1.5-1.4 1.7-1.6a2 2 0 0 1 2.7 0L21 15.6" />
  </svg>
);
const IcoCheck = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4.5 12.5l5 5 10-11" />
  </svg>
);

export default function FeedbackModal({ open, onClose, userName }: { open: boolean; onClose: () => void; userName?: string }) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [note, setNote] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const dragDepth = useRef(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // restore the draft each time the sheet opens; files stay live-synced
  useEffect(() => {
    if (!open) return;
    const d = loadDraft();
    setText(d.text);
    setKind(d.kind);
    setFiles(getDraftFiles());
    setPhase("idle");
    setNote(null);
    const t = setTimeout(() => textRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => subscribeDraftFiles(() => setFiles(getDraftFiles())), []);
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  const previews = useMemo(
    () => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [files],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const persist = useCallback((nextText: string, nextKind: FeedbackKind) => {
    saveDraft(nextText, nextKind);
  }, []);

  const attach = useCallback((incoming: File[]) => {
    const { rejected } = addDraftFiles(incoming);
    setNote(rejected ? REJECT_NOTE[rejected] : null);
  }, []);

  // paste-to-attach while the sheet is open (the launcher covers it while closed)
  useEffect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent) => {
      const picked = Array.from(e.clipboardData?.files ?? []).filter((f) =>
        /^(image|video)\//.test(f.type),
      );
      if (!picked.length) return;
      e.preventDefault();
      attach(picked);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open, attach]);

  // a file dropped ANYWHERE while the sheet is open is the sheet's: the page
  // behind must never navigate away to show it. The veil in the body just says so.
  useEffect(() => {
    if (!open) return;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes("Files");
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    const drop = (e: DragEvent) => {
      if (!e.dataTransfer?.files.length) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      attach(Array.from(e.dataTransfer.files));
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
      dragDepth.current = 0;
      setDragging(false);
    };
  }, [open, attach]);

  const removeFile = (idx: number) => {
    setDraftFiles(files.filter((_, i) => i !== idx));
    setNote(null);
  };

  const send = async () => {
    if (!text.trim() || phase === "sending" || phase === "sent") return;
    setPhase("sending");
    setNote(null);
    try {
      const fd = new FormData();
      fd.set("message", text.trim());
      fd.set("kind", kind);
      fd.set("page", window.location.pathname);
      fd.set(
        "meta",
        JSON.stringify({ viewport: `${window.innerWidth}×${window.innerHeight}` }),
      );
      for (const f of files) fd.append("files", f, f.name);
      const res = await fetch("/api/feedback", { method: "POST", body: fd });
      if (!res.ok) throw new Error(String(res.status));
      setPhase("sent");
      window.dispatchEvent(
        new CustomEvent("innernet:clip", {
          detail: { action: "note", name: "feedback sent, thank you" },
        }),
      );
      closeTimer.current = setTimeout(() => {
        onClose();
        // the sheet is still sinking: the draft goes once it is gone, so the
        // words do not vanish from under the way down
        closeTimer.current = setTimeout(() => {
          clearDraft();
          setText("");
          setKind("bug");
        }, 320);
      }, 1200);
    } catch {
      setPhase("failed");
    }
  };

  const trimmed = userName?.trim();

  return (
    <Sheet
      open={open}
      onClose={onClose}
      labelledBy="fb-title"
      title={<>what&rsquo;s up?</>}
      size="md"
      height="tall"
      panelClassName="fb-sheet"
      footStart={trimmed ? <span className="fb-who">from {trimmed}</span> : undefined}
      footer={
        <PillButton
          type="button"
          className="fb-send"
          loading={phase === "sending"}
          disabled={!text.trim() || phase === "sent"}
          icon={phase === "sent" ? IcoCheck : undefined}
          onClick={() => void send()}
        >
          {phase === "sent" ? "it's with us" : "send it"}
        </PillButton>
      }
    >
      <div className="fb-body">
        <textarea
          ref={textRef}
          className="input-soft fb-text"
          placeholder="tell us what happened, or what you wish existed…"
          aria-label="what happened"
          value={text}
          disabled={phase === "sending" || phase === "sent"}
          onChange={(e) => {
            setText(e.target.value);
            persist(e.target.value, kind);
            if (phase === "failed") setPhase("idle");
          }}
        />

        <div className="fb-chips" role="radiogroup" aria-label="what kind of feedback">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className="chip"
              role="radio"
              aria-checked={kind === k}
              data-on={kind === k}
              onClick={() => { setKind(k); persist(text, k); }}
            >
              {k === "feature" ? "feature request" : k}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="fb-drop"
          data-has={files.length > 0}
          onClick={() => inputRef.current?.click()}
          aria-label="attach images or videos"
        >
          <span className="fb-drop-ico" aria-hidden>{IcoMedia}</span>
          <span>
            {files.length
              ? `${files.length} of ${MAX_FILES} attached, add another`
              : "add a screenshot or clip"}
          </span>
          <span className="fb-drop-hint">click, drop, or paste. images or video</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={(e) => {
            attach(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />

        {previews.length > 0 && (
          <div className="fb-previews">
            {previews.map((p, i) => (
              <div key={p.url} className="fb-thumb">
                {p.file.type.startsWith("video/") ? (
                  <video src={p.url} muted playsInline preload="metadata" />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={p.url} alt={p.file.name} />
                )}
                <button
                  type="button"
                  className="fb-thumb-x"
                  aria-label={`remove ${p.file.name}`}
                  onClick={() => removeFile(i)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {note && <p className="fb-note">{note}</p>}
        {phase === "failed" && <p className="fb-note fb-note--warn">couldn&rsquo;t send. try again, everything&rsquo;s still here.</p>}

        {dragging && (
          <div className="fb-dropveil" aria-hidden>
            <span>drop it here</span>
          </div>
        )}
      </div>
    </Sheet>
  );
}
